"use client";
import React, { forwardRef, memo, useEffect, useImperativeHandle, useState, useRef } from "react";
import { autorun } from "mobx";

import { useTranslation } from "@/i18n/client";
import { useStores } from "@/models";

import cookieUtils from "@/utils/cookieUtils";
import { userSwitchCountry } from "@/utils/multiSite";

import { Country, Language, Currency } from "@/apis/address/address.interface";

import { Radio, RadioGroup, FormControlLabel, Select, MenuItem, SelectChangeEvent, CircularProgress, Typography } from "@mui/material";
import CountrySelect from "@/components/countrySelect";
import { CountrySelectRef } from "@/components/countrySelect";
import UKToast from "@/components/UKToast";

import { LANG, CURRENCY } from "@/utils/const";

import CustomiseWrapper from "./style";


interface IProps {
  type: number; // 0为头部组件入口 1为设置语言货币页面 2为设置国家页面
}

const Customise = forwardRef((props: IProps, ref) => {
  const { type } = props;
  const { t } = useTranslation();
  const { countryStore } = useStores();

  const [loading, setLoading] = useState<boolean>(false);

  const [languageType, setLanguageType] = useState<string>(); // 当前语言代码
  const [languageData, setLanguageData] = useState<Language[]>([]); // 语言列表

  const [currencyType, setCurrencyType] = useState<string>(); // 当前货币代码
  const [currentCurrencyData, setCurrentCurrencyData] = useState<Currency | null>(); // 当前货币数据
  const [currencyData, setCurrencyData] = useState<Currency[]>([]); // 货币列表

  const [currentCountryCode, setCurrentCountryCode] = useState<string>(); // 当前国家代码
  const [currentCountryData, setCurrentCountryData] = useState<Country | null>(); // 当前国家数据
  const [countryList, setCountryList] = useState<Country[]>([]); // 国家列表

  const countrySelectRef = useRef<CountrySelectRef>(null);

  useEffect(() => {
    // 监听countryStore
    const disposer = autorun(() => {
      setLoading(true);
      
      if (countryStore.countryList && countryStore.countryList.length > 0) {
        setCountryList(countryStore.countryList);
        setCurrentCountryCode(countryStore.currentCountryCode);
        setCurrentCountryData(countryStore.currentCountryData);
      }
      
      if (countryStore.supportLanguageList && countryStore.supportLanguageList.length > 0) {
        setLanguageData(countryStore.supportLanguageList);
        setLanguageType(countryStore.currentLanguageCode);
      }
      
      if (countryStore.supportCurrencyList && countryStore.supportCurrencyList.length > 0) {
        setCurrencyData(countryStore.supportCurrencyList);
        setCurrencyType(countryStore.currentCurrencyCode);
        setCurrentCurrencyData(countryStore.currentCurrencyData);
      }
      setLoading(false);
    });

    return () => {
      disposer();
    };
  }, []);

  const isSamePath = (type: string) => {
    const pathname = window.location.pathname;
    if (pathname !== "/settings/customise") return false;
    
    try {
      const params = new URLSearchParams(window.location.search);
      const typeParam = params.get("type");
      
      return typeParam === type;
    } catch (error) {
      console.error("解析URL参数异常:", error);
      return false;
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const code = (event.target as HTMLInputElement).value;
    const langConfig = languageData.find((lang) => lang.code === code);
    if (!langConfig) return;
    setLanguageType(langConfig.code);

    if (type) return;
    cookieUtils.setCookie(LANG, langConfig.code, 90);

    window.location.reload();
  };

  const handleCurrencyChange = (event: SelectChangeEvent) => {
    const code = event.target.value;
    const currencyInfo = currencyData.find((currency) => currency.code === code);
    if (!currencyInfo) return;
    setCurrencyType(code);
    setCurrentCurrencyData(currencyInfo);
  };

  const handleCancel = () => window.history.back();
  const handleLangAndCurrencyConfirm = () => {
    // 保存语言选择
    const langConfig = languageData.find((lang) => lang.code === languageType);
    if (!langConfig) return;
    cookieUtils.setCookie(LANG, langConfig.code, 90);
    
    // 保存货币选择
    const currencyConfig = currencyData.find((currency) => currency.code === currencyType);
    if (!currencyConfig) return;
    cookieUtils.setCookie(CURRENCY, currencyConfig.code, 90);

    UKToast.show({
      content: t("setting.customise.successResult") as any,
      icon: "success",
      duration: 3000,
    });

    setTimeout(() => {
      // const urlParams = new URLSearchParams(window.location.search);
      // const returnUrl = urlParams.get("returnUrl");

      // if (returnUrl) {
      //   window.location.href = decodeURIComponent(returnUrl);
      // } else {
      //   window.location.href = "/";
      // }
      window.location.reload();
    }, 1000);
  };

  const handleGoChange = (type: string) => {
    const flag = isSamePath(type);
    if (flag) return;
    
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete("returnUrl");
    const returnUrl = encodeURIComponent(currentUrl.toString());
    window.location.href = `/settings/customise?type=${type}&returnUrl=${returnUrl}`;
  };

  const handleCountryChange = (country: Country) => {
    if (country) {
      setCurrentCountryCode(country.countryCode);
      setCurrentCountryData(country);
    }
  };

  const handleCountryConfirm = async () => {
    const toCountry = currentCountryData?.countryCode || "";
    userSwitchCountry(toCountry);
  }

  useImperativeHandle(ref, () => ({
    languageData,
  }));

  const headerComponentRender = () => {
    return (
      <>
        {currentCurrencyData && (
          <div>
            <h4 className="title">{t("setting.customise.currency")}</h4>
            <span className="content-title">{currentCurrencyData?.displayText}</span>
            <div className="change-btn-box">
              <span className="change-btn" onClick={() => handleGoChange("1")}>
                {t("setting.customise.changeCurrency")}
              </span>
            </div>
          </div>
        )}

        {currentCountryData && (
          <div>
            <h4 className="title">{t("setting.customise.country")}</h4>
            {currentCountryData?.flagImage && (<img src={currentCountryData.flagImage} alt="" />)}
            <span className="content-title title-margin">{currentCountryData?.name ?? ""}</span>
            <div className="change-btn-box">
              <span className="change-btn" onClick={() => handleGoChange("2")}>
                {t("setting.customise.changeCountry")}
              </span>
            </div>
          </div>
        )}
      </>
    )
  }

  const langAndCurrencyRender = () => {
    return (
      <>
        <div>
          <div className="line"></div>
          <h4 className="title">{t("setting.customise.currency")}</h4>
          <Select 
            id="select" 
            key={currencyType || 'default-currency'}
            value={currencyType || ''}
            onChange={handleCurrencyChange}
            disabled={currencyData?.length < 2}
            renderValue={(value) => {
              const selectedCurrency = currencyData.find(item => item.code === value);
              return selectedCurrency ? (
                <div>
                  <span className="content-title title-margin">{selectedCurrency.displayText}</span>
                </div>
              ) : '';
            }}
          >
            {currencyData.map((item) => (
              <MenuItem value={item.code} key={item.code}>
                <span className="content-title title-margin">{item.displayText}</span>
              </MenuItem>
            ))}
          </Select>
        </div>
        
        {(languageData.length > 1 || currencyData.length > 1) && (
          <>
            <div className="line"></div>
            <div id="footer">
              <button className="btn cancel" onClick={handleCancel}>
                {t("cart.cancel")}
              </button>
              <button className="btn confirm" onClick={handleLangAndCurrencyConfirm}>
                {t("cart.confirm")}
              </button>
            </div>
          </>
        )}
      </>
    )
  }

  const countryComponentRender = () => {
    return (
      <>
        <div>
          <h4 className="title">{t("setting.customise.country")}</h4>
          <div className="countryInfoContainer">
            <div>
              <CountrySelect 
                ref={countrySelectRef}
                value={currentCountryCode} 
                onChange={handleCountryChange}
              />
              {/* 添加新标签页提示 */}
              <Typography variant="body2" sx={{ marginTop: '15px', fontSize: '0.9rem' }}>
                {t("setting.customise.newTabNote") ? (
                  <>
                    {(() => {
                      const text = t("setting.customise.newTabNote");
                      const colonIndex = text.indexOf(':');
                      if (colonIndex !== -1) {
                        return (
                          <>
                            <span style={{ fontWeight: 'bold' }}>
                              {text.substring(0, colonIndex + 1)}
                            </span>
                            {text.substring(colonIndex + 1)}
                          </>
                        );
                      } else {
                        // Fallback if no colon is found
                        return text;
                      }
                    })()}
                  </>
                ) : (
                  <>
                    <span style={{ fontWeight: 'bold' }}>NOTE:</span> A new country/region website will open in a new tab
                  </>
                )}
              </Typography>
            </div>

            {/* 显示提醒文字 */}
            <div className="countryAlert">
              <Typography variant="body2" fontWeight="bold" sx={{ marginBottom: '8px' }}>
                {t("setting.customise.changeCountryTitle")}
              </Typography>
              <Typography variant="body2">
                {t("setting.customise.changeCountryDescription")}
              </Typography>
            </div>
          </div>
        </div>
        
        <div className="line"></div>
        <div id="footer">
          <button className="btn cancel" onClick={handleCancel}>
            {t("cart.cancel")}
          </button>
          <button className="btn confirm" onClick={handleCountryConfirm}>
            {t("cart.confirm")}
          </button>
        </div>
      </>
    )
  }

  return (
    <CustomiseWrapper $type={type}>
      {loading ? (
        <div className="loadingContainer">
          <CircularProgress color="inherit" />
        </div>
      ) : (
        <>
          {type === 2 ? (
            countryComponentRender()
          ) : (
            <>
              <div>
                <h4 className="title">{t("setting.customise.language")}</h4>
                {languageType && (
                  <>
                    {/* 当type=1时（设置页面）使用多列布局 */}
                    {type === 1 ? (
                      <div className="language-grid-container">
                        {/* 将语言数据按每5个分组 */}
                        {Array.from({ length: Math.ceil((languageData?.length || 0) / 5) }, (_, columnIndex) => (
                          <div className="language-column" key={`column-${columnIndex}`}>
                            <RadioGroup
                              aria-labelledby="demo-radio-buttons-group-label"
                              value={languageType}
                              name="radio-buttons-group"
                              className="content-title"
                              onChange={handleChange}
                            >
                              {/* 每列显示5个语言选项 */}
                              {languageData
                                ?.slice(columnIndex * 5, columnIndex * 5 + 5)
                                .map((config) => (
                                  <FormControlLabel 
                                    value={config.code} 
                                    control={<Radio />} 
                                    label={config.displayText} 
                                    key={config.code} 
                                  />
                                ))}
                            </RadioGroup>
                          </div>
                        ))}
                      </div>
                    ) : (
                      /* 其他情况使用原来的单列布局 */
                      <RadioGroup
                        aria-labelledby="demo-radio-buttons-group-label"
                        value={languageType}
                        name="radio-buttons-group"
                        className="content-title"
                        onChange={handleChange}
                      >
                        {languageData?.map((config) => (
                          <FormControlLabel value={config.code} control={<Radio />} label={config.displayText} key={config.code} />
                        ))}
                      </RadioGroup>
                    )}
                  </>
                )}
              </div>
              
              {type === 0 && (
                headerComponentRender()
              )}

              {type === 1 && (
                langAndCurrencyRender()
              )}
            </>
          )}
        </>
      )}
    </CustomiseWrapper>
  );
});

Customise.displayName = "Customise";

export default memo(Customise);
